import { Box, Checkbox } from '@mui/material';
import { useAtom } from 'jotai';
import * as React from 'react';
import { type SyntheticEvent, useState } from 'react';

import DeviceDiffViewer from '../DeviceDiffViewer/DeviceDiffViewer';
import DeviceSyncRow from '../DeviceSyncRow/DeviceSyncRow';
import { useSyncDevicesDialog } from '../SyncDevices.hooks';
import {
  type DeviceAccordionRowProps,
  type DeviceSyncDataModel,
  type GroupedDevicesSyncData,
  groupedSyncItemsAtom
} from '../SyncDevices.types';
import useStyles from '../SyncDevicesForm.styles';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary
} from '../styled/Accordion.styled';

const DeviceAccordionRow = (props: DeviceAccordionRowProps) => {
  const { deviceRow } = props;
  const { classes } = useStyles();

  const { mode } = useSyncDevicesDialog();
  const [expanded, setExpanded] = useState<string | false>();
  const [checked, setChecked] = React.useState(deviceRow.isSelected);
  const [groupedSyncData, setGroupedSyncData] = useAtom(groupedSyncItemsAtom);

  const updateDeviceSyncSelection = (
    devRow: DeviceSyncDataModel,
    isChecked: boolean
  ) => {
    const updatedGrpSyncData = groupedSyncData.map(
      (row: GroupedDevicesSyncData) => {
        if (devRow.orgName === row.organization) {
          const updatedItems =
            row.items?.map((item) =>
              item.hostName === devRow.hostName
                ? { ...item, isSelected: isChecked }
                : item
            ) ?? [];

          return {
            ...row,
            items: updatedItems,
            selectedCount: updatedItems.filter((item) => item.isSelected).length
          };
        }
        return row;
      }
    );
    setGroupedSyncData(updatedGrpSyncData);
  };

  const handleCheckboxChange = (event: React.MouseEvent) => {
    event.stopPropagation();
    const ischecked = (event.target as HTMLInputElement).checked;
    setChecked(ischecked);
    updateDeviceSyncSelection(deviceRow, ischecked);
  };

  const handleChange =
    (panel: string) => (event: SyntheticEvent, newExpanded: boolean) => {
      event.stopPropagation();
      setExpanded(newExpanded ? panel : false);
    };

  return (
    <Accordion
      className={classes.syncAccordion}
      expanded={
        expanded ===
        `panel-${deviceRow?.devices?.host_name || deviceRow?.devices_deployed?.host_name}`
      }
      onChange={handleChange(
        `panel-${deviceRow?.devices?.host_name || deviceRow?.devices_deployed?.host_name}`
      )}
    >
      <AccordionSummary
        aria-controls="panel1d-content"
        id="panel-{deviceRow.devices?.display_name || deviceRow.devices_deployed?.host_name}"
        sx={{
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            position: 'relative'
          }
        }}
      >
        <DeviceSyncRow
          host_name={
            deviceRow.devices?.display_name ||
            deviceRow.devices?.host_name ||
            deviceRow.devices_deployed?.host_name ||
            ''
          }
          device_type={
            deviceRow.devices?.device_type?.short_code ??
            deviceRow.devices_deployed?.device_type?.short_code ??
            ''
          }
          action_type={deviceRow.actionType || ''}
        />

        {(mode === 'newSyncDevices' || mode === 'confirmSyncDevices') && (
          <Box className={classes.deviceCheckboxAlign}>
            <Checkbox
              style={{ padding: '0' }}
              checked={checked}
              onClick={handleCheckboxChange}
            />
          </Box>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <DeviceDiffViewer
          host_name={
            deviceRow.devices?.host_name ||
            deviceRow.devices_deployed?.host_name ||
            ''
          }
          device_size={deviceRow.devices?.device_size?.device_size || ''}
          configuration={deviceRow.devices?.configuration || ''}
          prod_device_size={
            deviceRow.devices_deployed?.device_size?.device_size || ''
          }
          prod_configuration={deviceRow.devices_deployed?.configuration || ''}
          action_type={deviceRow?.actionType || ''}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default DeviceAccordionRow;
